import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import { isClient, isExecutor, isOwner, isLastOwner, hasPartnerRelation } from '@core/utils/task/taskOwnership';
import { getStatus } from '@core/utils/task/taskStatusToShow';
import { Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from 'Context/LanguageContext';
import { useAnimation, motion } from 'framer-motion';
import PropTypes from 'prop-types';

const StatusContainer = styled(motion(Box))(({ theme, background, color }) => ({
  fontSize: '1em',
  position: 'relative',
  width: '90%',
  height: '90%',
  borderRadius: 7,
  padding: '5%',
  justifyContent: 'center',
  cursor: 'default',
  color: color || theme.palette.text.primary,
  backgroundColor: background || theme.palette.background.default,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
}));
const TaskStatus = ({ task, status, setStatusParent }) => {
  const lan = useLanguage();
  const user = useSelector(selectUser);
  const controls = useAnimation();
  const prevTextRef = useRef();
  const statusData = useMemo(() => {
    let statusInfo;
    if (task) {
      const taskOwnership = {
        isClient: isClient(user, task),
        isExecutor: isExecutor(user, task),
        isOwner: isOwner(user, task),
        isLastOwner: isLastOwner(user, task),
        hasPartnerRelation: hasPartnerRelation(user, task),
      };
      statusInfo = getStatus(task.status, taskOwnership);
    } else statusInfo = status;
    if (!statusInfo) return null;
    return {
      text: lan[statusInfo.text] || statusInfo.text,
      background: statusInfo.background,
      color: statusInfo.color,
    };
  }, [task, status, user, lan]);
  useEffect(() => {
    if (statusData && setStatusParent) setStatusParent(statusData.text);
  }, [statusData, setStatusParent]);
  useEffect(() => {
    if (!statusData?.text) return;
    if (prevTextRef.current && prevTextRef.current !== statusData.text) {
      controls
        .start({
          scale: [1, 2, 2, 1, 1],
          rotate: [0, 0, 15, 15, 0],
        })
        .then(r => console.log(r));
    }
    prevTextRef.current = statusData.text;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData?.text]);
  if (!statusData) return null;
  return (
    <StatusContainer
      background={statusData.background}
      color={statusData.color}
      animate={controls}
      key={task ? `task-status-${task.idTask}-${statusData.text}` : statusData.text}
    >
      <Typography
        sx={{
          fontFamily: 'Lato, sans-serif',
          lineHeight: 1.3,
          fontSize: 12,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          textAlign: 'center',
        }}
      >
        {statusData.text}
      </Typography>
    </StatusContainer>
  );
};
TaskStatus.propTypes = {
  task: PropTypes.object,
  status: PropTypes.any,
  setStatusParent: PropTypes.func,
};

export default TaskStatus;
